<template>
  <div class="grid text-left pr-5 col-start-auto">
    <h5 class="h5 !font-bold self-start flex items-center">
      <span class="cursor-pointer px-4" @click="goBack">
        <ChevronDown class="rotate-90 !fill-black size-4" />
      </span>
      Checkout
    </h5>
    <div class="relative grow min-h-[290px] mt-5">
      <div
        v-show="paymentType === 'creditCard' && !props.paymentSuccessful && !props.processing"
        id="payment-element"
        class="w-full h-full"
        :class="{ 'opacity-50 pointer-events-none disabled': isFreeWithCoupon() }">
        <!--Stripe.js injects the Payment Element-->
      </div>
    </div>
    <div class="flex gap-5 max-sm:mt-5">
      <div class="w-full">
        <BaseInput
          v-model:value="code"
          class="w-full rounded-md"
          name="discountCode"
          placeholder="Discount code"
          :error-message="
            promotionCode.codeValidation?.value?.valid === false ? 'Code is invalid' : ''
          " />
        <div class="h-6 mt-1">
          <p v-if="promotionCode?.codeValidation?.value?.valid" class="paragraph-2 text-right">
            Discount code applied!
          </p>
        </div>
      </div>
      <button
        class="border border-grey-3 rounded-sm h-[46px] w-24 button-3 disabled:opacity-50"
        :disabled="promotionCode.isValidatingCode.value || !code"
        data-test="coupon-code-apply"
        @click="validateCode">
        {{ promotionCode.isValidatingCode.value ? 'Validating...' : 'Apply' }}
      </button>
    </div>
    <div v-if="promotionCode?.codeValidation?.value?.valid" class="text-right paragraph-2"></div>
    <div v-if="props.error" class="text-secondary paragraph-2">
      {{ props.error }}
    </div>
    <div class="border border-blue-5 text-blue-1 p-4 rounded self-start grow-0 space-y-4">
      <p class="button-2">Order summary:</p>
      <p class="flex justify-between paragraph-2">
        {{ capitalize(props.selectedTier?.product?.name ?? '') }}
        <span class="text-right">{{
          promotionCode.formattedPrice(props.selectedTier?.price?.amount ?? 0)
        }}</span>
      </p>
      <hr class="my-3 text-grey-5" />
      <div
        v-if="promotionCode?.codeValidation?.value?.valid"
        class="flex justify-between paragraph-2">
        <span>Discount:</span>
        <span class="text-right">{{ promotionCode.formatDiscount() }}</span>
      </div>
      <p class="flex justify-between paragraph-2">
        Total (GST included):
        <span class="text-right">{{
          promotionCode.calculateFinalPrice(props.selectedTier?.price.amount ?? 0)
        }}</span>
      </p>
    </div>

    <OtivoButton
      id="confirm-payment-button"
      :disabled="!props.selectedTier || props.processing || props.paymentSuccessful"
      :loading="props.processing"
      class="w-full self-end mt-5"
      data-test="confirm-subscription"
      @click="attemptPayment"
      >{{ isFreeWithCoupon() ? 'Confirm subscription' : 'Confirm and pay' }}
    </OtivoButton>
  </div>
</template>

<script setup lang="ts">
import { capitalize, ref } from 'vue'
import OtivoButton from '@/components/OtivoButton.vue'
import BaseInput from '@/components/Inputs/BaseInput.vue'
import ChevronDown from '@/components/icons/ChevronDown.vue'
import { PlanTier } from '@/composables/useTieredPlans.ts'
import { usePromotionCode } from '@/composables/usePromotionCode'

type Props = {
  selectedTier: PlanTier | null
  processing: boolean
  paymentSuccessful: boolean
  error: string | null
}
const props = defineProps<Props>()
const emit = defineEmits<{
  'go-back': []
  'attempt-payment': [
    paymentType: string,
    promoCode?: string,
    discount?: {
      percent_off: number | null
      amount_off: number | null
      currency: string | null
    } | null,
  ]
}>()

const paymentType = ref('creditCard')
const code = ref('')
const promotionCode = usePromotionCode()

const attemptPayment = () => {
  const validCode = promotionCode.codeValidation.value?.valid ? code.value : undefined
  const paymentTypeToUse = isFreeWithCoupon() ? 'coupon' : paymentType.value
  const discount = promotionCode.codeValidation.value?.valid
    ? promotionCode.codeValidation.value.discount
    : null
  emit('attempt-payment', paymentTypeToUse, validCode, discount)
}

const goBack = () => {
  emit('go-back')
}

const validateCode = () => {
  promotionCode.validatePromotionCode(code.value)
}

const isFreeWithCoupon = () => {
  const originalPrice = props.selectedTier?.price.amount ?? 0
  const discount = promotionCode.codeValidation.value?.discount

  if (!discount || !promotionCode.codeValidation.value?.valid) {
    return false
  }

  if (discount.percent_off && discount.percent_off >= 100) {
    return true
  }

  return discount.amount_off && discount.amount_off >= originalPrice
}
</script>

<style scoped></style>
